import type { ApiErrorBody } from "./types";

/**
 * Centralized API client. Every server call in the app goes through `api`:
 * base URL, cookies (Better Auth session), JSON, request ids, tenant headers and error
 * normalization live here. Page components never call fetch().
 *
 * The transport is swappable: the browser `fetch` transport talks to the Rogue backend; the
 * mock transport (src/api/mock, enabled with VITE_USE_MOCK_API=true) serves demo data.
 */

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
export type QueryValue = string | number | boolean | null | undefined | Array<string | number>;

export interface ApiRequest {
  method: HttpMethod;
  path: string;
  query?: Record<string, QueryValue>;
  body?: unknown;
  headers?: Record<string, string>;
  signal?: AbortSignal;
}

export interface TransportResponse {
  status: number;
  body: unknown;
}

export interface Transport {
  send(request: ApiRequest & { headers: Record<string, string> }): Promise<TransportResponse>;
  /** File responses (e.g. the lead import template). Errors come back as a JSON `body`. */
  download?(request: ApiRequest & { headers: Record<string, string> }): Promise<{ status: number; blob: Blob | null; body: unknown }>;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly requestId: string | null;
  readonly details: unknown;

  constructor(opts: { status: number; code: string; message: string; requestId?: string | null; details?: unknown }) {
    super(opts.message);
    this.name = "ApiError";
    this.status = opts.status;
    this.code = opts.code;
    this.requestId = opts.requestId ?? null;
    this.details = opts.details;
  }

  get isUnauthenticated() {
    return this.status === 401;
  }
  get isForbidden() {
    return this.status === 403;
  }
  get isNotFound() {
    return this.status === 404 && !this.isUnavailable;
  }
  get isNetwork() {
    return this.status === 0;
  }
  /**
   * The endpoint itself does not exist on this backend yet (a [planned] endpoint), as opposed
   * to a record that was not found.
   */
  get isUnavailable() {
    return this.status === 501 || (this.status === 404 && this.code === "ROUTE_NOT_FOUND");
  }

  /** Short, human, safe message for toasts and inline errors. Never a stack trace. */
  get userMessage(): string {
    if (this.isNetwork) return "Can't reach Rogue. Check your connection and try again.";
    if (this.isUnauthenticated) return "Your session has ended. Sign in again.";
    if (this.isForbidden) return "You don't have permission to do that for this store.";
    if (this.isUnavailable) return "This isn't available on the server yet.";
    if (this.status >= 500) return `Something went wrong on our side${this.requestId ? ` (ref ${this.requestId.slice(-8)})` : ""}.`;
    return this.message || "The request could not be completed.";
  }
}

// ── tenant scope (set by the store context) ─────────────────────────────────
export interface TenantScope {
  organizationId: string | null;
  /** A store id, or "ALL" for every accessible store (from the URL); null before sign-in. */
  storeId: string | null;
}
let tenantScope: TenantScope = { organizationId: null, storeId: null };
export function setTenantScope(scope: TenantScope) {
  tenantScope = scope;
}
export function getTenantScope(): TenantScope {
  return tenantScope;
}

// ── fetch transport ─────────────────────────────────────────────────────────
export function buildUrl(baseUrl: string, path: string, query?: Record<string, QueryValue>): string {
  const url = new URL(`${baseUrl}${path}`, typeof window === "undefined" ? "http://localhost" : window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      if (value.length) url.searchParams.set(key, value.join(","));
    } else {
      url.searchParams.set(key, String(value));
    }
  }
  return baseUrl ? url.toString() : `${url.pathname}${url.search}`;
}

export function createFetchTransport(baseUrl: string): Transport {
  return {
    async send(req) {
      let response: Response;
      try {
        response = await fetch(buildUrl(baseUrl, req.path, req.query), {
          method: req.method,
          headers: req.headers,
          // FormData (file uploads) is sent as-is so the browser sets the multipart boundary.
          body: req.body === undefined ? undefined : req.body instanceof FormData ? req.body : JSON.stringify(req.body),
          credentials: "include",
          signal: req.signal,
        });
      } catch (err) {
        if ((err as Error).name === "AbortError") throw err;
        return { status: 0, body: null };
      }
      const text = await response.text();
      let body: unknown = null;
      if (text) {
        try {
          body = JSON.parse(text);
        } catch {
          body = { error: { code: "INVALID_RESPONSE", message: "Unexpected response from the server" } };
        }
      }
      return { status: response.status, body };
    },
    async download(req) {
      let response: Response;
      try {
        response = await fetch(buildUrl(baseUrl, req.path, req.query), {
          method: req.method,
          headers: req.headers,
          credentials: "include",
          signal: req.signal,
        });
      } catch (err) {
        if ((err as Error).name === "AbortError") throw err;
        return { status: 0, blob: null, body: null };
      }
      if (response.ok) return { status: response.status, blob: await response.blob(), body: null };
      const body = await response.json().catch(() => null);
      return { status: response.status, blob: null, body };
    },
  };
}

// ── client ──────────────────────────────────────────────────────────────────
export function newRequestId(): string {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeError(status: number, body: unknown, requestId: string): ApiError {
  // Rogue errors are { error: { code, message } }; Better Auth (/api/auth/*) answers { code, message }.
  const b = body as (ApiErrorBody & { code?: string; message?: string }) | null;
  const err =
    b?.error ??
    (b && typeof b.message === "string" ? { code: b.code ?? `HTTP_${status}`, message: b.message, requestId: undefined, details: undefined } : undefined);
  // The Hono backend answers unknown routes with 404 "Route not found".
  const routeMissing = status === 404 && err?.message === "Route not found";
  return new ApiError({
    status,
    code: routeMissing ? "ROUTE_NOT_FOUND" : (err?.code ?? (status === 0 ? "NETWORK" : `HTTP_${status}`)),
    message: err?.message ?? (status === 0 ? "Network error" : `Request failed (${status})`),
    requestId: err?.requestId ?? requestId,
    details: err?.details,
  });
}

export interface ApiClient {
  request<T>(req: ApiRequest): Promise<T>;
  get<T>(path: string, opts?: { query?: Record<string, QueryValue>; signal?: AbortSignal }): Promise<T>;
  post<T>(path: string, body?: unknown, opts?: { headers?: Record<string, string> }): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  delete<T>(path: string): Promise<T>;
  /** A file download (GET), as a Blob. */
  download(path: string, opts?: { query?: Record<string, QueryValue> }): Promise<Blob>;
}

export type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();
export function onUnauthorized(listener: UnauthorizedListener) {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export function createApiClient(transport: Transport): ApiClient {
  async function request<T>(req: ApiRequest): Promise<T> {
    const requestId = newRequestId();
    const headers: Record<string, string> = { Accept: "application/json", "X-Request-Id": requestId, ...req.headers };
    if (req.body !== undefined && !(req.body instanceof FormData)) headers["Content-Type"] = "application/json";
    const scope = getTenantScope();
    if (scope.organizationId) headers["X-Organization-Id"] = scope.organizationId;
    if (scope.storeId && !req.path.startsWith("/api/auth")) headers["X-Store-Id"] = scope.storeId;

    const res = await transport.send({ ...req, headers });
    if (res.status >= 200 && res.status < 300) return res.body as T;
    const error = normalizeError(res.status, res.body, requestId);
    if (error.isUnauthenticated && !req.path.startsWith("/api/auth")) {
      for (const l of unauthorizedListeners) l();
    }
    throw error;
  }
  return {
    request,
    get: (path, opts) => request({ method: "GET", path, query: opts?.query, signal: opts?.signal }),
    post: (path, body, opts) => request({ method: "POST", path, body, headers: opts?.headers }),
    put: (path, body) => request({ method: "PUT", path, body }),
    patch: (path, body) => request({ method: "PATCH", path, body }),
    delete: (path) => request({ method: "DELETE", path }),
    async download(path, opts) {
      const requestId = newRequestId();
      const headers: Record<string, string> = { "X-Request-Id": requestId };
      const scope = getTenantScope();
      if (scope.storeId) headers["X-Store-Id"] = scope.storeId;
      if (!transport.download) throw new ApiError({ status: 501, code: "NOT_IMPLEMENTED", message: "Downloads aren't supported here" });
      const res = await transport.download({ method: "GET", path, query: opts?.query, headers });
      if (res.blob) return res.blob;
      throw normalizeError(res.status, res.body, requestId);
    },
  };
}

export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}

/** Human message for any thrown value. */
export function errorMessage(err: unknown): string {
  if (isApiError(err)) return err.userMessage;
  return "Something unexpected happened.";
}
